/**
 * Amenity Pin Service
 *
 * Unified service for all community amenity pins on the map.
 * Supports: porta potties, taco trucks, burger joints, coffee shops,
 * shake shops, bakeries, vape shops, marijuana dispensaries, auto services, etc.
 *
 * All pins use the same /api/poop-pins endpoint with different pin_type values.
 */

import { telegram } from './telegram';

// All supported amenity types with their display info
export const AMENITY_TYPES = {
  // Restrooms - all use poop emoji 💩
  porta_potty: { emoji: '💩', label: 'Porta Potty', category: 'restroom' },
  restroom: { emoji: '💩', label: 'Restroom', category: 'restroom' },

  // Food & Drink
  taco: { emoji: '🌮', label: 'Taco Truck', category: 'food' },
  burger: { emoji: '🍔', label: 'Burger Joint', category: 'food' },
  coffee: { emoji: '☕', label: 'Coffee Shop', category: 'food' },
  shake: { emoji: '🥤', label: 'Shake Shop', category: 'food' },
  bakery: { emoji: '🧁', label: 'Bakery', category: 'food' },
  food_truck: { emoji: '🍽️', label: 'Food Truck', category: 'food' },

  // Services
  automotive: { emoji: '🔧', label: 'Auto Service', category: 'service' },
  parts: { emoji: '⚙️', label: 'Parts Service', category: 'service' },
  delivery: { emoji: '🚚', label: 'Delivery Service', category: 'service' },

  // Specialty Shops
  vape: { emoji: '💨', label: 'Vape Shop', category: 'shop' },
  marijuana: { emoji: '🌿', label: 'Dispensary', category: 'shop' },

  // Oil field basics
  fuel: { emoji: '⛽', label: 'Fuel', category: 'oilfield' },
  water: { emoji: '💧', label: 'Water', category: 'oilfield' },
  supplies: { emoji: '📦', label: 'Supplies', category: 'oilfield' },
} as const;

export type AmenityType = keyof typeof AMENITY_TYPES;

// Filter categories for the FAB cycle
// Index 0 = default (all amenities shown on load)
export const AMENITY_FILTER_CATEGORIES = [
  { key: 'all', emoji: '🏪', label: 'All Amenities' },    // Default: all shown
  { key: 'taco', emoji: '🌮', label: 'Taco Shops' },
  { key: 'porta_potty', emoji: '💩', label: 'Bathrooms' },
  { key: 'vape', emoji: '💨', label: 'Vape Shops' },
  { key: 'marijuana', emoji: '🌿', label: 'Weed Shops' },
  { key: 'automotive', emoji: '🔧', label: 'Auto Shops' },
  { key: 'none', emoji: '📌', label: 'Hide Amenities' },  // Last: hide all
] as const;

export type AmenityFilterCategory = typeof AMENITY_FILTER_CATEGORIES[number]['key'];

// Amenity types available for quick-add via the ? FAB
export const QUICK_ADD_AMENITIES = [
  { type: 'porta_potty', emoji: '💩', label: 'Porta Potty' },
  { type: 'taco', emoji: '🌮', label: 'Taco Truck' },
  { type: 'burger', emoji: '🍔', label: 'Burger Joint' },
  { type: 'coffee', emoji: '☕', label: 'Coffee Shop' },
  { type: 'shake', emoji: '🥤', label: 'Shake Shop' },
  { type: 'bakery', emoji: '🧁', label: 'Bakery' },
  { type: 'automotive', emoji: '🔧', label: 'Auto Service' },
  { type: 'vape', emoji: '💨', label: 'Vape Shop' },
  { type: 'marijuana', emoji: '🌿', label: 'Dispensary' },
] as const;

export interface AmenityPin {
  id: string;
  latitude: number;
  longitude: number;
  created_at: string;
  active_until: string | null;
  expires_at: string;
  telegram_user_id?: string | null;
  telegram_username?: string | null;
  note?: string | null;
  name?: string | null;
  pin_type: string;
  upvotes: number;
  downvotes: number;
  score: number;
  votes?: number;
  is_ghost: boolean;
  ghost_at: string | null;
  status?: 'active' | 'ghost';
  emoji?: string;
  // User's current vote state - prevents double-voting
  userVote?: 'up' | 'down' | null;
  userUpvoted?: boolean;
  userDownvoted?: boolean;
}

const SANTA_FE_RESTROOM_TEXT_PATTERNS = [
  'santa fe rd',
  'santa fe road',
  'e santa fe',
  'east santa fe',
] as const;

const SANTA_FE_RESTROOM_CLEANUP_ZONE = {
  minLat: 32.7726,
  maxLat: 32.7732,
  minLng: -103.1257,
  maxLng: -103.1250,
} as const;

function normalizeAmenityLocationText(value?: string | null): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[.,#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isSantaFeRestroomPin(pin: {
  id?: string | null;
  pin_type?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  note?: string | null;
  name?: string | null;
  location_context?: string | null;
  locationContext?: string | null;
}): boolean {
  const pinType = String(pin.pin_type || '').toLowerCase();
  if (pinType !== 'porta_potty' && pinType !== 'restroom') return false;

  const text = normalizeAmenityLocationText([
    pin.id,
    pin.note,
    pin.name,
    pin.location_context,
    pin.locationContext,
  ].filter(Boolean).join(' '));
  if (SANTA_FE_RESTROOM_TEXT_PATTERNS.some((pattern) => text.includes(pattern))) return true;

  const lat = typeof pin.latitude === 'string' ? Number.parseFloat(pin.latitude) : Number(pin.latitude);
  const lng = typeof pin.longitude === 'string' ? Number.parseFloat(pin.longitude) : Number(pin.longitude);
  return Number.isFinite(lat)
    && Number.isFinite(lng)
    && lat >= SANTA_FE_RESTROOM_CLEANUP_ZONE.minLat
    && lat <= SANTA_FE_RESTROOM_CLEANUP_ZONE.maxLat
    && lng >= SANTA_FE_RESTROOM_CLEANUP_ZONE.minLng
    && lng <= SANTA_FE_RESTROOM_CLEANUP_ZONE.maxLng;
}

class AmenityPinService {
  private baseUrl = '/api';

  private normalizePin(raw: any): AmenityPin {
    if (!raw) {
      throw new Error('Invalid amenity pin payload');
    }

    const pinType = raw.pin_type || 'other';
    const amenityInfo = AMENITY_TYPES[pinType as AmenityType];

    return {
      id: raw.id,
      latitude: typeof raw.latitude === 'string' ? parseFloat(raw.latitude) : raw.latitude,
      longitude: typeof raw.longitude === 'string' ? parseFloat(raw.longitude) : raw.longitude,
      created_at: raw.created_at,
      active_until: raw.active_until ?? null,
      expires_at: raw.expires_at,
      telegram_user_id: raw.telegram_user_id ?? raw.user_id ?? null,
      telegram_username: raw.telegram_username ?? raw.user_name ?? null,
      note: raw.note ?? null,
      name: raw.name ?? null,
      pin_type: pinType,
      upvotes: raw.upvotes ?? 0,
      downvotes: raw.downvotes ?? 0,
      score: raw.score ?? raw.votes ?? 0,
      votes: raw.votes ?? raw.score ?? 0,
      is_ghost: Boolean(raw.is_ghost),
      ghost_at: raw.ghost_at ?? null,
      status: raw.status ?? (raw.is_ghost ? 'ghost' : 'active'),
      emoji: raw.emoji ?? amenityInfo?.emoji ?? '📍',
      // User's current vote state - prevents double-voting
      userVote: raw.userVote ?? null,
      userUpvoted: raw.userUpvoted ?? false,
      userDownvoted: raw.userDownvoted ?? false,
    };
  }

  private withAuthHeaders(base: Record<string, string> = {}): Record<string, string> {
    const headers: Record<string, string> = { ...base };
    try {
      const initData = telegram && typeof telegram.getInitData === 'function' ? telegram.getInitData() : undefined;
      if (initData) {
        headers['X-Telegram-Init-Data'] = initData;
      }
      const userId = telegram && typeof telegram.getUserId === 'function' ? telegram.getUserId() : undefined;
      if (userId) {
        headers['X-Telegram-User-Id'] = userId;
      }
      const user = telegram && typeof telegram.getUser === 'function' ? telegram.getUser() : undefined;
      const username = user?.username || user?.first_name;
      if (username) {
        headers['X-Telegram-Username'] = username;
      }
    } catch {
      // Ignore missing Telegram context
    }
    return headers;
  }

  /**
   * Report a new amenity pin at the given location
   */
  async reportPin(
    latitude: number,
    longitude: number,
    pinType: AmenityType,
    options?: {
      userId?: string;
      userName?: string;
      name?: string;
      note?: string;
    }
  ): Promise<AmenityPin> {
    const response = await fetch(`${this.baseUrl}/poop-pins`, {
      method: 'POST',
      headers: this.withAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        latitude,
        longitude,
        pinType,
        user_id: options?.userId,
        user_name: options?.userName || 'Anonymous',
        telegramUserId: options?.userId,
        telegramUsername: options?.userName || 'Anonymous',
        name: options?.name,
        note: options?.note,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Failed to report amenity pin: ${response.status} ${errorText}`);
    }

    const data = await response.json();

    if (!data?.success || !data.pin) {
      throw new Error('Failed to parse amenity pin response');
    }

    return this.normalizePin(data.pin);
  }

  /**
   * Get all amenity pins nearby, optionally filtered by type
   */
  async getPinsNearby(
    latitude: number,
    longitude: number,
    radiusKm: number = 50,
    pinType?: AmenityType | 'all'
  ): Promise<AmenityPin[]> {
    let url = `${this.baseUrl}/poop-pins?lat=${latitude}&lng=${longitude}&radius=${radiusKm}`;
    if (pinType && pinType !== 'all') {
      url += `&type=${pinType}`;
    }

    const response = await fetch(url, { headers: this.withAuthHeaders() });

    if (!response.ok) {
      throw new Error(`Failed to fetch amenity pins: ${response.statusText}`);
    }

    const data = await response.json();

    if (Array.isArray(data)) {
      return data.filter((pin: any) => !isSantaFeRestroomPin(pin)).map(pin => this.normalizePin(pin));
    }

    if (data?.pins) {
      return data.pins.filter((pin: any) => !isSantaFeRestroomPin(pin)).map((pin: any) => this.normalizePin(pin));
    }

    return [];
  }

  /**
   * Get pins filtered by type
   */
  async getPinsByType(
    latitude: number,
    longitude: number,
    pinType: AmenityType,
    radiusKm: number = 50
  ): Promise<AmenityPin[]> {
    const allPins = await this.getPinsNearby(latitude, longitude, radiusKm);
    return allPins.filter(pin => pin.pin_type === pinType);
  }

  /**
   * Vote on a pin
   */
  async votePin(pinId: string, vote: 'up' | 'down' | 'none' = 'up'): Promise<AmenityPin> {
    const response = await fetch(`${this.baseUrl}/poop-pins/vote`, {
      method: 'POST',
      headers: this.withAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        pinId,
        vote,
        telegramUserId: this.getUserId(),
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to vote on amenity pin: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data?.success || !data.pin) {
      throw new Error('Failed to parse amenity pin vote response');
    }

    return this.normalizePin(data.pin);
  }

  async upvotePin(pinId: string): Promise<AmenityPin> {
    return this.votePin(pinId, 'up');
  }

  async downvotePin(pinId: string): Promise<AmenityPin> {
    return this.votePin(pinId, 'down');
  }

  private getUserId(): string | undefined {
    try {
      const userId = telegram && typeof telegram.getUserId === 'function' ? telegram.getUserId() : undefined;
      return userId ?? undefined;
    } catch {
      return undefined;
    }
  }

  async deletePin(pinId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/poop-pins/${pinId}`, {
      method: 'DELETE',
      headers: this.withAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete amenity pin: ${response.statusText}`);
    }
  }

  /**
   * Get emoji for a pin type
   */
  getEmoji(pinType: string): string {
    const info = AMENITY_TYPES[pinType as AmenityType];
    return info?.emoji ?? '📍';
  }

  /**
   * Get label for a pin type
   */
  getLabel(pinType: string): string {
    const info = AMENITY_TYPES[pinType as AmenityType];
    return info?.label ?? 'Location';
  }
}

export const amenityPinService = new AmenityPinService();
